'use strict';
/*
  captions.js — turn word-level timestamps into render-ready cues.

  Input words: [{ w:"hello", s:1.2, e:1.5 }, ...]
  Output cue:  { t, end, anim, emph, lines:[ { tokens:[ {t, emph} ] } ] }

  A "cue" is one on-screen card (a phrase). Words are grouped by silence gaps,
  a max word count and a max duration, then wrapped into balanced lines.
*/

const FILLER = new Set(['the', 'a', 'an', 'to', 'of', 'and', 'is', 'it', 'i', 'in', 'on',
  'og', 'at', 'en', 'et', 'jeg', 'er', 'med', 'på', 'det', 'den', 'som', 'for']);

function groupWords(words, opt) {
  const maxWords = opt.maxWords || 6;
  const maxDur = opt.maxDur || 3.2;
  const gapSplit = opt.gapSplit || 0.55;
  const groups = [];
  let cur = [];
  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    if (cur.length) {
      const prev = cur[cur.length - 1];
      const gap = w.s - prev.e;
      const dur = w.e - cur[0].s;
      if (gap > gapSplit || cur.length >= maxWords || dur > maxDur) { groups.push(cur); cur = []; }
    }
    cur.push(w);
  }
  if (cur.length) groups.push(cur);
  return groups;
}

// choose the most "important" word index in a group to emphasise
function emphIndex(group) {
  let best = 0, bestScore = -1;
  for (let i = 0; i < group.length; i++) {
    const raw = group[i].w.replace(/[^\p{L}\p{N}]/gu, '');
    if (!raw) continue;
    const lower = raw.toLowerCase();
    let score = raw.length;                      // longer = more salient
    if (FILLER.has(lower)) score -= 5;
    if (/[A-ZÆØÅ]/.test(raw[0]) && i > 0) score += 2; // mid-sentence capital ~ proper noun
    if (/[!?]/.test(group[i].w)) score += 3;
    if (score > bestScore) { bestScore = score; best = i; }
  }
  return best;
}

// wrap tokens into <=maxPerLine, aiming for 2 balanced lines max for short cues
function wrapLines(tokens, maxCharsPerLine, maxLines) {
  const lines = [];
  let line = [], len = 0;
  for (const tk of tokens) {
    const wl = tk.t.length + 1;
    if (line.length && len + wl > maxCharsPerLine) { lines.push(line); line = []; len = 0; }
    line.push(tk); len += wl;
  }
  if (line.length) lines.push(line);
  // if we exceeded maxLines, redistribute as evenly as possible
  if (maxLines && lines.length > maxLines) {
    const flat = lines.flat();
    const per = Math.ceil(flat.length / maxLines);
    const re = [];
    for (let i = 0; i < flat.length; i += per) re.push(flat.slice(i, i + per));
    return re;
  }
  return lines;
}

function build(words, opt = {}) {
  const anims = opt.anims && opt.anims.length ? opt.anims : ['slam', 'drop', 'punch', 'split'];
  const upper = opt.upper !== false;
  const maxChars = opt.maxCharsPerLine || 16;
  const maxLines = opt.maxLines || 3;
  const groups = groupWords(words, opt);
  const cues = [];
  groups.forEach((g, gi) => {
    const eIdx = emphIndex(g);
    const tokens = g.map((w, i) => {
      let t = w.w.replace(/\s+/g, '').trim();
      if (upper) t = t.toUpperCase();
      return { t, emph: i === eIdx };
    }).filter(tk => tk.t.length);
    if (!tokens.length) return;
    const lines = wrapLines(tokens, maxChars, maxLines).map(toks => ({ tokens: toks }));
    cues.push({
      t: g[0].s,
      end: g[g.length - 1].e,
      anim: anims[gi % anims.length],
      lines,
      emphWord: (tokens.find(tk => tk.emph) || tokens[0]).t,
    });
  });
  return cues;
}

// ---- input adapters ----------------------------------------------------

// Plain lyric / caption text with NO timing: distribute evenly across duration.
function fromPlainText(text, dur, opt = {}) {
  const words = [];
  const rawWords = text.replace(/\[.*?\]/g, ' ').split(/\s+/).filter(Boolean);
  const n = rawWords.length || 1;
  const per = dur / n;
  rawWords.forEach((w, i) => words.push({ w, s: +(i * per).toFixed(2), e: +((i + 1) * per).toFixed(2) }));
  return build(words, opt);
}

// Whisper-style segments [{start,end,text,words:[{w,s,e}]}] -> flat words
function wordsFromSegments(segments) {
  const out = [];
  for (const seg of segments) {
    if (seg.words && seg.words.length) {
      for (const w of seg.words) out.push({ w: (w.w || w.word || '').trim(), s: w.s ?? w.start, e: w.e ?? w.end });
    } else if (seg.text) {
      // no word timing: spread across the segment
      const ws = seg.text.trim().split(/\s+/);
      const per = (seg.end - seg.start) / (ws.length || 1);
      ws.forEach((w, i) => out.push({ w, s: seg.start + i * per, e: seg.start + (i + 1) * per }));
    }
  }
  return out.filter(w => w.w);
}

module.exports = { build, fromPlainText, wordsFromSegments, groupWords };
